import { useHTTP } from "@soft-db/http/vue";
import { ScopeType, UserScope } from "@soft-db/monitoring-api-client";
import { defineStore } from "pinia";
import { useCurrentUserRole, useCurrentUserScope, useScopeDetails } from "../api/current-user";
import { useStaticQueryParam } from "./static-query";

export type OverwriteType = "distributor" | "space" | "user";

export const useScopeStore = defineStore("scope", () => {
	const overwriteDistId = useStaticQueryParam<string | null>("distributor", {
		defaultVal: null,
	});
	const overwriteSpaceId = useStaticQueryParam<string | null>("space", {
		defaultVal: null,
	});
	// const overwriteUserId = useStaticQueryParam<string | null>("user", { defaultVal: null });

	const { hasRole } = useCurrentUserRole();
	const { scope: currentUserScope } = useCurrentUserScope();
	const http = useHTTP();

	const spaceScope = computed<UserScope | null>(() => {
		if (overwriteSpaceId.value != null) {
			return { type: "space", id: overwriteSpaceId.value };
		}

		return userScopeIf("space");
	});

	const distributorScope = computed<UserScope | null>(() => {
		if (overwriteDistId.value != null) {
			return { type: "distributor", id: overwriteDistId.value };
		}

		return userScopeIf("distributor");
	});

	const effectiveScope = computed<UserScope>(() => {
		if (spaceScope.value) return spaceScope.value;
		if (distributorScope.value) return distributorScope.value;

		return { type: "none", id: "" };
	});

	const { scope: effectiveScopeDetails } = useScopeDetails(effectiveScope);

	const isHost = computed(
		() =>
			effectiveScopeDetails.value?.type === "distributor" &&
			effectiveScopeDetails.value.details.isHost
	);

	const activeDistributorId = computed(() => {
		return distributorScope.value?.id || "";
	});

	const activeSpaceId = computed(() => {
		return spaceScope.value?.id || "";
	});

	function resetContext() {
		overwriteDistId.value = null;
		overwriteSpaceId.value = null;
		// overwriteUserId.value = null;
	}

	const router = useRouter();
	async function setContext(type: OverwriteType, id: string | null) {
		await router.push("/");

		if (currentUserScope.value?.type !== "distributor") {
			return;
		}

		if (type === "distributor" && !hasRole("sys_admin")) {
			return;
		}

		if (type === "distributor" || type === "user") {
			resetContext();
		}

		switch (type) {
			case "distributor":
				overwriteDistId.value = id;
				break;
			case "space":
				overwriteSpaceId.value = id;
				break;
			case "user":
				// overwriteUserId.value = id;
				break;
		}
	}

	function isScope(type: ScopeType) {
		return effectiveScope.value.type === type;
	}

	function userScopeIf(type: ScopeType): UserScope | null {
		return currentUserScope.value?.type === type ? currentUserScope.value : null;
	}

	http.onRequest((_, config) => {
		if (overwriteDistId.value) {
			config.headers["x-api-distributor-overwrite"] = overwriteDistId.value;
		}

		if (overwriteSpaceId.value) {
			config.headers["x-api-space-overwrite"] = overwriteSpaceId.value;
		}

		// if (overwriteUserId.value) {
		// 	config.headers["x-api-user-overwrite"] = overwriteUserId.value;
		// }

		return config;
	});

	return {
		scope: effectiveScope,
		isHost,

		distributorScope,
		distributorId: activeDistributorId,

		spaceScope,
		spaceId: activeSpaceId,

		overwriteDistId,
		overwriteSpaceId,
		// overwriteUserId,

		setContext,
		resetContext,

		isScope,
	};
});
